import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminAction,
  AdminActionCategory,
  AdminAsyncJobCreationResponse,
  AdminFieldKey,
  AdminJobResponse,
  adminActionCategories,
  adminActions,
  getAdminJob,
  getDefaultAdminActionValues,
  runAdminAction,
} from 'services/admin';
import styles from './AdminTools.styles';

type TaskStatus = 'running' | 'success' | 'error';

type Task = {
  id: string;
  actionLabel: string;
  status: TaskStatus;
  startedAt: string;
  startedAtMs: number;
  finishedAt?: string;
  finishedAtMs?: number;
  message: string;
  links: string[];
};

const PASS_STORAGE_KEY = 'crv-admin-tools-unlocked';
const ADMIN_PASSPHRASE =
  process.env.REACT_APP_ADMIN_TOOLS_PASSPHRASE || 'crv-admin';
const ASYNC_DIGITAL_PDF_ACTION_ID = 'catalog-all-digital';
const JOB_POLL_INTERVAL_MS = 5000;
const JOB_POLL_TIMEOUT_MS = 60 * 60 * 1000;

const statusColor: Record<TaskStatus, 'default' | 'success' | 'error' | 'warning'> = {
  running: 'warning',
  success: 'success',
  error: 'error',
};

const statusLabel: Record<TaskStatus, string> = {
  running: 'Executando',
  success: 'Concluído',
  error: 'Erro',
};

const getInitialValues = (action: AdminAction) => ({
  ...getDefaultAdminActionValues(action),
});

const formatTime = () => new Date().toLocaleTimeString('pt-BR');

const formatElapsedTime = (elapsedMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }

  return `${minutes}:${paddedSeconds}`;
};

const stringifyResponse = (data: unknown) => {
  if (!data) return 'Solicitação concluída.';
  if (typeof data === 'string') return data;

  try {
    return JSON.stringify(data, null, 2);
  } catch (err) {
    return 'Solicitação concluída.';
  }
};

const stringifyJobError = (error: unknown) => (
  error === undefined || error === null
    ? 'Job finalizado com erro.'
    : stringifyResponse(error)
);

const extractLinks = (data: unknown): string[] => {
  const links = new Set<string>();

  const visit = (value: unknown) => {
    if (!value) return;

    if (typeof value === 'string') {
      if (/^https?:\/\//i.test(value)) links.add(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([ key, entry ]) => {
        if (/url|path|link/i.test(key)) visit(entry);
        if (typeof entry === 'object') visit(entry);
      });
    }
  };

  visit(data);
  return Array.from(links);
};

const isCanceledRequest = (err: unknown) => (
  axios.isCancel(err) ||
  (axios.isAxiosError(err) && err.code === 'ERR_CANCELED')
);

const getErrorMessage = (err: unknown) => {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED') {
      return 'Tempo limite atingido antes da API responder.';
    }

    const status = err.response?.status;
    const payload = err.response?.data;
    const payloadText = typeof payload === 'string'
      ? payload
      : payload
        ? JSON.stringify(payload)
        : err.message;

    return status ? `HTTP ${status}: ${payloadText}` : payloadText;
  }

  return err instanceof Error ? err.message : 'Erro desconhecido.';
};

const isAsyncJobCreationResponse = (
  data: unknown
): data is AdminAsyncJobCreationResponse => {
  if (!data || typeof data !== 'object') return false;

  const payload = data as Partial<AdminAsyncJobCreationResponse>;

  return payload.ok === true &&
    typeof payload.jobId === 'string' &&
    payload.jobId.length > 0 &&
    payload.status === 'queued';
};

const getRunnerMessage = (runner: AdminAsyncJobCreationResponse['runner']) => {
  if (runner.started) {
    return runner.operationName
      ? `Runner iniciado: ${runner.operationName}.`
      : 'Runner iniciado.';
  }

  return `Runner não iniciado: ${runner.reason}.`;
};

const getProcessingMessage = (
  jobId: string,
  status: AdminJobResponse['status'],
  runner?: AdminAsyncJobCreationResponse['runner']
) => [
  `Job ${jobId} em processamento.`,
  `Status: ${status}.`,
  runner ? getRunnerMessage(runner) : '',
].filter(Boolean).join('\n');

const AdminTools: FC = () => {
  const [ unlocked, setUnlocked ] = useState(
    window.sessionStorage.getItem(PASS_STORAGE_KEY) === 'true'
  );
  const [ passphrase, setPassphrase ] = useState('');
  const [ unlockError, setUnlockError ] = useState('');
  const [ category, setCategory ] = useState<AdminActionCategory>('bull');
  const [ actionId, setActionId ] = useState(adminActions[0].id);
  const selectedAction = useMemo(
    () => adminActions.find(action => action.id === actionId) || adminActions[0],
    [ actionId ]
  );
  const categoryActions = useMemo(
    () => adminActions.filter(action => action.category === category),
    [ category ]
  );
  const [ values, setValues ] = useState<Partial<Record<AdminFieldKey, string | boolean>>>(
    getInitialValues(selectedAction)
  );
  const [ files, setFiles ] = useState<File[]>([]);
  const [ tasks, setTasks ] = useState<Task[]>([]);
  const [ formError, setFormError ] = useState('');
  const [ now, setNow ] = useState(Date.now());
  const mountedRef = useRef(true);
  const pollTimeoutsRef = useRef<Record<string, number>>({});
  const pollAbortControllersRef = useRef<Record<string, AbortController>>({});

  const clearJobPolling = (taskId: string) => {
    const timeoutId = pollTimeoutsRef.current[taskId];
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      delete pollTimeoutsRef.current[taskId];
    }

    const controller = pollAbortControllersRef.current[taskId];
    if (controller) {
      controller.abort();
      delete pollAbortControllersRef.current[taskId];
    }
  };

  useEffect(() => {
    const firstAction = categoryActions[0];
    if (firstAction && !categoryActions.some(action => action.id === actionId)) {
      setActionId(firstAction.id);
    }
  }, [ actionId, categoryActions ]);

  useEffect(() => {
    setValues(getInitialValues(selectedAction));
    setFiles([]);
    setFormError('');
  }, [ selectedAction ]);

  useEffect(() => {
    if (!tasks.some(task => task.status === 'running')) return;

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [ tasks ]);

  useEffect(() => () => {
    mountedRef.current = false;

    Object.keys(pollTimeoutsRef.current).forEach(taskId => {
      window.clearTimeout(pollTimeoutsRef.current[taskId]);
    });

    Object.values(pollAbortControllersRef.current).forEach(controller => {
      controller.abort();
    });
  }, []);

  const handleUnlock = (event: FormEvent) => {
    event.preventDefault();

    if (passphrase !== ADMIN_PASSPHRASE) {
      setUnlockError('Senha inválida.');
      return;
    }

    window.sessionStorage.setItem(PASS_STORAGE_KEY, 'true');
    setUnlocked(true);
    setUnlockError('');
  };

  const handleValueChange = (
    key: AdminFieldKey,
    value: string | boolean
  ) => {
    setValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(event.target.files || []));
  };

  const validate = () => {
    if (selectedAction.requiresFile && files.length === 0) {
      return 'Selecione pelo menos um arquivo.';
    }

    const missingField = selectedAction.fields?.find(field =>
      field.required &&
      field.type !== 'boolean' &&
      !values[field.key]
    );

    if (missingField) {
      return `Preencha o campo "${missingField.label}".`;
    }

    return '';
  };

  const updateTask = (taskId: string, update: Partial<Task>) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, ...update } : task
    ));
  };

  const finishTask = (taskId: string, update: Partial<Task>) => {
    clearJobPolling(taskId);
    updateTask(taskId, {
      finishedAt: formatTime(),
      finishedAtMs: Date.now(),
      ...update,
    });
  };

  const pollAdminJob = (
    taskId: string,
    action: AdminAction,
    jobId: string,
    startedAtMs: number
  ) => {
    const elapsedMs = Date.now() - startedAtMs;
    if (elapsedMs >= JOB_POLL_TIMEOUT_MS) {
      finishTask(taskId, {
        status: 'error',
        message: `Tempo limite atingido aguardando o job ${jobId}. Consulte /bull/jobs/${jobId} para verificar o estado atual.`,
      });
      return;
    }

    const controller = new AbortController();
    pollAbortControllersRef.current[taskId] = controller;

    getAdminJob(action, jobId, controller.signal)
      .then(response => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (pollAbortControllersRef.current[taskId] === controller) {
          delete pollAbortControllersRef.current[taskId];
        }

        const job = response.data;

        if (job.status === 'success') {
          finishTask(taskId, {
            status: 'success',
            message: stringifyResponse(job.result),
            links: extractLinks(job.result),
          });
          return;
        }

        if (job.status === 'error') {
          finishTask(taskId, {
            status: 'error',
            message: stringifyJobError(job.error),
          });
          return;
        }

        if (job.status === 'queued' || job.status === 'running') {
          updateTask(taskId, {
            status: 'running',
            message: getProcessingMessage(jobId, job.status),
          });
          scheduleJobPoll(taskId, action, jobId, startedAtMs);
          return;
        }

        finishTask(taskId, {
          status: 'error',
          message: `Status inesperado do job ${jobId}: ${String(job.status)}.`,
        });
      })
      .catch(err => {
        if (!mountedRef.current || controller.signal.aborted || isCanceledRequest(err)) return;
        if (pollAbortControllersRef.current[taskId] === controller) {
          delete pollAbortControllersRef.current[taskId];
        }

        finishTask(taskId, {
          status: 'error',
          message: getErrorMessage(err),
        });
      });
  };

  const scheduleJobPoll = (
    taskId: string,
    action: AdminAction,
    jobId: string,
    startedAtMs: number
  ) => {
    const timeoutId = pollTimeoutsRef.current[taskId];
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }

    pollTimeoutsRef.current[taskId] = window.setTimeout(() => {
      delete pollTimeoutsRef.current[taskId];
      pollAdminJob(taskId, action, jobId, startedAtMs);
    }, JOB_POLL_INTERVAL_MS);
  };

  const handleRunAction = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (
      selectedAction.dangerous &&
      !window.confirm(`Confirmar execução: ${selectedAction.label}?`)
    ) {
      return;
    }

    setFormError('');

    const taskId = `${Date.now()}`;
    const startedAtMs = Date.now();
    const startedAt = formatTime();
    setNow(startedAtMs);
    setTasks(prev => [
      {
        id: taskId,
        actionLabel: selectedAction.label,
        status: 'running',
        startedAt,
        startedAtMs,
        message: 'Aguardando resposta da API.',
        links: [],
      },
      ...prev,
    ]);

    try {
      const action = selectedAction;
      const response = await runAdminAction({
        action,
        values,
        files,
      });

      if (!mountedRef.current) return;

      if (action.id === ASYNC_DIGITAL_PDF_ACTION_ID) {
        if (!isAsyncJobCreationResponse(response.data)) {
          throw new Error(`Resposta inesperada ao criar job: ${stringifyResponse(response.data)}`);
        }

        updateTask(taskId, {
          status: 'running',
          message: getProcessingMessage(
            response.data.jobId,
            response.data.status,
            response.data.runner
          ),
        });
        scheduleJobPoll(taskId, action, response.data.jobId, startedAtMs);
        return;
      }

      updateTask(taskId, {
        status: 'success',
        finishedAt: formatTime(),
        finishedAtMs: Date.now(),
        message: stringifyResponse(response.data),
        links: extractLinks(response.data),
      });
    } catch (err) {
      if (!mountedRef.current) return;

      updateTask(taskId, {
        status: 'error',
        finishedAt: formatTime(),
        finishedAtMs: Date.now(),
        message: getErrorMessage(err),
      });
    }
  };

  if (!unlocked) {
    return (
      <Box sx={ styles.page }>
        <Container maxWidth="sm">
          <Stack spacing={ 3 } sx={ styles.panel } component="form" onSubmit={ handleUnlock }>
            <Box>
              <Typography variant="h5" fontWeight={ 700 }>
                CRV Operações
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acesso local
              </Typography>
            </Box>

            { unlockError && <Alert severity="error">{ unlockError }</Alert> }

            <TextField
              label="Senha"
              type="password"
              value={ passphrase }
              onChange={ event => setPassphrase(event.target.value) }
              autoFocus
              fullWidth
            />

            <Button type="submit" variant="contained" size="large">
              Entrar
            </Button>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={ styles.page }>
      <Container maxWidth="lg">
        <Stack spacing={ 3 }>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={ 2 }
            sx={ styles.header }
          >
            <Box>
              <Typography variant="h4" fontWeight={ 700 }>
                CRV Operações
              </Typography>
              <Typography variant="body2" color="text.secondary">
                API Hub e API Catálogo
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={ () => {
                window.sessionStorage.removeItem(PASS_STORAGE_KEY);
                setUnlocked(false);
                setPassphrase('');
              } }
            >
              Bloquear
            </Button>
          </Stack>

          <Box sx={ styles.panel }>
            <Stack spacing={ 3 }>
              <Box sx={ styles.fieldGrid }>
                <TextField
                  select
                  label="Área"
                  value={ category }
                  onChange={ event => setCategory(event.target.value as AdminActionCategory) }
                  fullWidth
                >
                  { adminActionCategories.map(option => (
                    <MenuItem key={ option.id } value={ option.id }>
                      { option.label }
                    </MenuItem>
                  )) }
                </TextField>

                <TextField
                  select
                  label="Operação"
                  value={ selectedAction.id }
                  onChange={ event => setActionId(event.target.value) }
                  fullWidth
                >
                  { categoryActions.map(action => (
                    <MenuItem key={ action.id } value={ action.id }>
                      { action.label }
                    </MenuItem>
                  )) }
                </TextField>
              </Box>

              <Alert severity={ selectedAction.dangerous ? 'warning' : 'info' }>
                { selectedAction.description }
              </Alert>

              { selectedAction.fields && selectedAction.fields.length > 0 && (
                <Box sx={ styles.fieldGrid }>
                  { selectedAction.fields.map(field => {
                    if (field.type === 'boolean') {
                      return (
                        <FormControlLabel
                          key={ field.key }
                          control={
                            <Checkbox
                              checked={ Boolean(values[field.key]) }
                              onChange={ event =>
                                handleValueChange(field.key, event.target.checked)
                              }
                            />
                          }
                          label={ field.label }
                        />
                      );
                    }

                    return (
                      <TextField
                        key={ field.key }
                        select={ field.type === 'select' }
                        label={ field.label }
                        value={ values[field.key] ?? '' }
                        onChange={ event =>
                          handleValueChange(field.key, event.target.value)
                        }
                        helperText={ field.helperText }
                        required={ field.required }
                        fullWidth
                      >
                        { field.options?.map(option => (
                          <MenuItem key={ option.value } value={ option.value }>
                            { option.label }
                          </MenuItem>
                        )) }
                      </TextField>
                    );
                  }) }
                </Box>
              ) }

              { selectedAction.requiresFile && (
                <Stack spacing={ 1.5 }>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1.5 } alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button variant="outlined" component="label">
                      Selecionar arquivo
                      <input
                        hidden
                        type="file"
                        multiple={ selectedAction.multipleFiles }
                        accept={ selectedAction.fileAccept }
                        onChange={ handleFilesChange }
                      />
                    </Button>

                    <Typography variant="body2" color="text.secondary">
                      { files.length > 0
                        ? `${files.length} arquivo${files.length > 1 ? 's' : ''} selecionado${files.length > 1 ? 's' : ''}`
                        : 'Nenhum arquivo selecionado' }
                    </Typography>
                  </Stack>

                  { files.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      { files.map(file => (
                        <Chip key={ `${file.name}-${file.size}` } label={ file.name } />
                      )) }
                    </Box>
                  ) }
                </Stack>
              ) }

              { formError && <Alert severity="error">{ formError }</Alert> }

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1.5 }>
                <Button variant="contained" size="large" onClick={ handleRunAction }>
                  Executar
                </Button>
                <Button
                  variant="text"
                  onClick={ () => {
                    setValues(getInitialValues(selectedAction));
                    setFiles([]);
                    setFormError('');
                  } }
                >
                  Limpar
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Box sx={ styles.panel }>
            <Stack spacing={ 2 }>
              <Typography variant="h6" fontWeight={ 700 }>
                Execuções
              </Typography>

              { tasks.some(task => task.status === 'running') && <LinearProgress /> }

              { tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma execução iniciada.
                </Typography>
              ) : (
                <Stack spacing={ 1.5 }>
                  { tasks.map(task => {
                    const elapsedMs = (task.finishedAtMs ?? now) - task.startedAtMs;

                    return (
                      <Stack key={ task.id } spacing={ 1 } sx={ styles.taskRow }>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={ 1 }
                        >
                          <Typography variant="subtitle2" fontWeight={ 700 }>
                            { task.actionLabel }
                          </Typography>
                          <Stack direction="row" spacing={ 1 } alignItems="center">
                            <Chip
                              size="small"
                              variant="outlined"
                              label={ `Tempo ${formatElapsedTime(elapsedMs)}` }
                            />
                            <Chip
                              size="small"
                              color={ statusColor[task.status] }
                              label={ statusLabel[task.status] }
                            />
                          </Stack>
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                          Início: { task.startedAt }
                          { task.finishedAt ? ` | Fim: ${task.finishedAt}` : '' }
                        </Typography>

                        { task.links.length > 0 && (
                          <Stack spacing={ 0.75 }>
                            { task.links.map(link => (
                              <Button
                                key={ link }
                                href={ link }
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                size="small"
                                sx={{ alignSelf: 'flex-start', maxWidth: '100%', textTransform: 'none' }}
                              >
                                Abrir arquivo gerado
                              </Button>
                            )) }
                          </Stack>
                        ) }

                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: '#f6f8f7',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            maxHeight: 420,
                          }}
                        >
                          { task.message }
                        </Box>
                      </Stack>
                    );
                  }) }
                </Stack>
              ) }
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default AdminTools;
